import React, { useState, useRef, useEffect } from 'react';
import './NativeCameraUpload.css';

function NativeCameraUpload({ onClose, onUpload }) {
  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploading, setUploading] = useState(0); // Count of currently uploading
  const [uploaded, setUploaded] = useState(0); // Count of successfully uploaded
  const [failed, setFailed] = useState(0); // Count of failed uploads
  const fileInputRef = useRef(null);

  // Process upload queue in background
  useEffect(() => {
    const processQueue = async () => {
      const pendingUploads = uploadQueue.filter(item => item.status === 'pending');
      
      for (const item of pendingUploads) {
        // Mark as uploading
        setUploadQueue(prev => prev.map(i => 
          i.id === item.id ? { ...i, status: 'uploading' } : i
        ));
        setUploading(prev => prev + 1);

        try {
          await onUpload(item.file);
          
          // Mark as uploaded
          setUploadQueue(prev => prev.map(i => 
            i.id === item.id ? { ...i, status: 'uploaded' } : i
          ));
          setUploaded(prev => prev + 1);
        } catch (err) {
          console.error('Upload error:', err);
          
          // Mark as failed
          setUploadQueue(prev => prev.map(i => 
            i.id === item.id ? { ...i, status: 'failed', error: err.message } : i
          ));
          setFailed(prev => prev + 1);
        } finally {
          setUploading(prev => prev - 1);
        }
      }
    };

    processQueue();
  }, [uploadQueue, onUpload]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;

    // Add files to upload queue
    const newItems = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      status: 'pending', // pending, uploading, uploaded, failed
      error: null
    }));

    setUploadQueue(prev => [...prev, ...newItems]);
    
    // Reset file input to allow selecting the same photo again
    e.target.value = '';
  };

  const triggerCamera = () => {
    fileInputRef.current?.click();
  };

  const pendingCount = uploadQueue.filter(i => i.status === 'pending').length;
  const totalInQueue = uploadQueue.length;

  return (
    <div className="native-camera-overlay">
      <div className="native-camera-modal">
        <div className="native-camera-header">
          <h2>📸 Take Photos</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <div className="native-camera-content">
          {/* Upload Status */}
          <div className="upload-status">
            {uploading > 0 && (
              <div className="status-uploading">
                <span className="spinner"></span>
                Uploading {uploading} photo{uploading > 1 ? 's' : ''}...
              </div>
            )}
            
            {uploaded > 0 && (
              <div className="status-success">
                ✅ {uploaded} photo{uploaded > 1 ? 's' : ''} uploaded!
              </div>
            )}
            
            {failed > 0 && (
              <div className="status-error">
                ❌ {failed} failed
              </div>
            )}
          </div>

          <div className="camera-instructions">
            <p>Tap the button below to take photos.</p>
            <p><strong>Take multiple photos quickly</strong> - they'll upload in the background!</p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          <button
            onClick={triggerCamera}
            className="take-photo-btn"
          >
            📷 Take Photo
          </button>

          {/* Queue Info */}
          {totalInQueue > 0 && (
            <div className="queue-info">
              <div className="queue-stats">
                <div className="stat-item">
                  <span className="stat-label">In Queue:</span>
                  <span className="stat-value">{pendingCount}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Uploading:</span>
                  <span className="stat-value">{uploading}</span>
                </div>
                <div className="stat-item success">
                  <span className="stat-label">Uploaded:</span>
                  <span className="stat-value">{uploaded}</span>
                </div>
                {failed > 0 && (
                  <div className="stat-item error">
                    <span className="stat-label">Failed:</span>
                    <span className="stat-value">{failed}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="native-camera-footer">
          <button 
            onClick={onClose} 
            className="done-btn"
            disabled={uploading > 0}
          >
            {uploading > 0 ? `Uploading ${uploading}...` : 'Done'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NativeCameraUpload;
